import React, { useRef, useMemo, useState } from 'react'
import classnames from 'classnames'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Datepicker from 'react-tailwindcss-datepicker'
import { ArrowLeftIcon, BuildingOffice2Icon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import Button from '../../../../../components/Form/Button'
import DisplayField from '../../../../../components/Form/DisplayField'
import Input from '../../../../../components/Form/Input'
import Panel from '../../../../../components/Panel'
import { getInputStyles } from '../../../../../components/Form/styles'
import { InstallmentConfigurator } from './InstallmentConfigurator'
import ConfirmDialogShell from './ConfirmDialogShell'
import {
    addMonthsClamped,
    computeSuggestedFirstDueDate,
    parseIsoDateLocal,
} from './installmentDates'
import { useTodayMidnight } from './useTodayMidnight'
import { createUtilityIncentivePlanApi } from '../../../../../apis/apis'
import { pushToast } from '../../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../../shared/redux/hooks'
import { formatDateForApi, getApiErrorMessage } from '../../../../../util/string'

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)

const formatShortDate = (iso: string) => {
    const datePart = iso.slice(0, 10)
    return new Date(datePart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

type AmountMode = 'total' | 'per-installment' | 'freeform'

interface LastKingInvoice {
    billingStartDate?: string
    billingEndDate?: string
    dueDate?: string
}

interface CreateUtilityPaymentPlanFormProps {
    tenantAccountId: string
    estimatedUtilityBalance: number | undefined
    hasCreateUtilityPlanPermission: boolean
    utilityName: string
    lastKingInvoice?: LastKingInvoice
    onSuccess: () => void
    onCancel: () => void
}

const COMMON_DURATIONS = [3, 6, 12, 18, 24]

const CreateUtilityPaymentPlanForm: React.FC<CreateUtilityPaymentPlanFormProps> = ({
    tenantAccountId,
    estimatedUtilityBalance = 0,
    hasCreateUtilityPlanPermission,
    utilityName,
    lastKingInvoice,
    onSuccess,
    onCancel,
}) => {
    const dispatch = useAppDispatch()
    const [customInstallments, setCustomInstallments] = useState<number[]>([])
    const [editedIndices, setEditedIndices] = useState<Set<number>>(new Set())
    const [amountMode, setAmountMode] = useState<AmountMode>('total')
    const [perInstallmentStr, setPerInstallmentStr] = useState('')
    const [showConfirm, setShowConfirm] = useState(false)
    const [showCustomDuration, setShowCustomDuration] = useState(false)
    const confirmLockedRef = useRef(false)

    const outstandingBalance = estimatedUtilityBalance ?? 0
    const suggestedFirstDueDate = useMemo(
        () => computeSuggestedFirstDueDate(lastKingInvoice?.dueDate),
        [lastKingInvoice?.dueDate]
    )
    const todayMidnight = useTodayMidnight()

    const validationSchema = useMemo(
        () =>
            Yup.object({
                duration: Yup.number()
                    .typeError('Duration must be a number')
                    .required('Duration is required')
                    .min(1, 'Minimum duration is 1 month')
                    .max(24, 'Maximum duration is 24 months')
                    .integer('Duration must be a whole number'),
                totalAmount: Yup.number()
                    .typeError('Total plan amount must be a number')
                    .required('Total plan amount is required')
                    .moreThan(0, 'Total amount must be greater than $0'),
                firstDueDate: Yup.string().required('First installment due date is required'),
            }),
        []
    )

    const formik = useFormik({
        initialValues: {
            duration: '12',
            totalAmount: outstandingBalance > 0 ? outstandingBalance.toFixed(2) : '',
            firstDueDate: suggestedFirstDueDate,
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            const durationNum = parseInt(values.duration)

            const effectiveTotal =
                amountMode === 'freeform'
                    ? Math.round(customInstallments.reduce((s, a) => s + a, 0) * 100) / 100
                    : amountMode === 'per-installment'
                        ? Math.round((parseFloat(perInstallmentStr) || 0) * durationNum * 100) / 100
                        : parseFloat(values.totalAmount)

            if (customInstallments.some((v) => v <= 0)) {
                dispatch(pushToast({ message: 'All installments must be greater than $0', type: 'error' }))
                return
            }

            if (amountMode === 'total') {
                const installmentsTotal = customInstallments.reduce((s, a) => s + a, 0)
                if (Math.abs(installmentsTotal - parseFloat(values.totalAmount)) > 0.005) {
                    dispatch(pushToast({ message: 'Installment total must equal plan balance', type: 'error' }))
                    return
                }
            }

            try {
                await createUtilityIncentivePlanApi(
                    {
                        startingBalance: effectiveTotal.toFixed(2),
                        cadence: 'MONTHLY',
                        installmentCount: durationNum,
                        ...(values.firstDueDate ? { startDate: values.firstDueDate } : {}),
                        installmentAmounts: customInstallments.map(a => a.toFixed(2)),
                    },
                    tenantAccountId
                )
                await onSuccess()
                dispatch(pushToast({ message: `${utilityName} payment plan created successfully`, type: 'success' }))
            } catch (e: any) {
                setShowConfirm(false)
                confirmLockedRef.current = false
                dispatch(
                    pushToast({
                        message: 'Failed to create payment plan',
                        type: 'error',
                        description: getApiErrorMessage(e),
                    })
                )
            }
        },
    })

    const perInstallmentTotal = useMemo(
        () => Math.round((parseFloat(perInstallmentStr) || 0) * (parseInt(formik.values.duration) || 0) * 100) / 100,
        [perInstallmentStr, formik.values.duration]
    )

    const freeformTotal = useMemo(
        () => Math.round(customInstallments.reduce((s, a) => s + a, 0) * 100) / 100,
        [customInstallments]
    )

    const effectiveTotalStr = useMemo(() => {
        if (amountMode === 'per-installment') return perInstallmentTotal.toString()
        if (amountMode === 'freeform') return freeformTotal.toString()
        return formik.values.totalAmount
    }, [amountMode, perInstallmentTotal, freeformTotal, formik.values.totalAmount])

    const firstDueDateObj = useMemo(
        () => parseIsoDateLocal(formik.values.firstDueDate),
        [formik.values.firstDueDate]
    )

    const { estimatedFirstDueDateStr, estimatedLastDueDateStr } = useMemo(() => {
        const duration = parseInt(formik.values.duration)
        const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        if (firstDueDateObj == null || isNaN(duration) || duration < 1) {
            return { estimatedFirstDueDateStr: null, estimatedLastDueDateStr: null }
        }
        return {
            estimatedFirstDueDateStr: fmt(firstDueDateObj),
            estimatedLastDueDateStr: fmt(addMonthsClamped(firstDueDateObj, duration - 1)),
        }
    }, [firstDueDateObj, formik.values.duration])

    const isFirstDueInPast = useMemo(() => {
        if (firstDueDateObj == null) return false
        return firstDueDateObj < todayMidnight
    }, [firstDueDateObj, todayMidnight])

    const exceedsBalance =
        estimatedUtilityBalance !== undefined &&
        (parseFloat(effectiveTotalStr) || 0) > outstandingBalance

    const hasInstallmentIssue = useMemo(() => {
        if (customInstallments.length === 0) return false
        const hasZero = customInstallments.some((v) => v <= 0)
        if (amountMode === 'freeform') {
            return hasZero || freeformTotal <= 0
        }
        const planTotal =
            amountMode === 'per-installment'
                ? perInstallmentTotal
                : parseFloat(formik.values.totalAmount) || 0
        const sum = customInstallments.reduce((s, a) => s + a, 0)
        const mismatch = amountMode === 'total' && planTotal > 0 && Math.abs(sum - planTotal) > 0.005
        return hasZero || mismatch || planTotal <= 0
    }, [customInstallments, formik.values.totalAmount, amountMode, perInstallmentStr, perInstallmentTotal, freeformTotal])

    const handleSubmitClick = async () => {
        const errors = await formik.validateForm()
        await formik.setTouched(Object.fromEntries(Object.keys(formik.values).map((k) => [k, true])))
        if (
            Object.keys(errors).length === 0 &&
            !hasInstallmentIssue &&
            !exceedsBalance &&
            !isFirstDueInPast
        ) {
            setShowConfirm(true)
        }
    }

    const handleConfirmSubmit = async () => {
        if (confirmLockedRef.current || formik.isSubmitting) return
        confirmLockedRef.current = true
        await formik.submitForm()
    }

    if (!hasCreateUtilityPlanPermission) {
        return (
            <div className="space-y-4">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center gap-1 rounded-md border border-indigo-600 dark:border-indigo-400 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Incentives
                </button>
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <ExclamationCircleIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">
                        You do not have permission to create {utilityName} payment plans.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <ConfirmDialogShell isOpen={showConfirm}>
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/40 shrink-0">
                        <BuildingOffice2Icon className="w-5 h-5 text-orange-700 dark:text-orange-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Create {utilityName} Payment Plan?</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Please confirm the details below before submitting.</p>
                    </div>
                </div>
                <div className="space-y-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
                    <div className="flex justify-between px-4 py-2">
                        <span className="text-gray-500 dark:text-gray-400">Amount</span>
                        <span className="font-medium dark:text-gray-200">{formatCurrency(parseFloat(effectiveTotalStr) || 0)}</span>
                    </div>
                    <div className="flex justify-between px-4 py-2">
                        <span className="text-gray-500 dark:text-gray-400">Duration</span>
                        <span className="font-medium dark:text-gray-200">{formik.values.duration} months</span>
                    </div>
                    {estimatedFirstDueDateStr && (
                        <div className="flex justify-between px-4 py-2">
                            <span className="text-gray-500 dark:text-gray-400">First Due</span>
                            <span className="font-medium dark:text-gray-200">{estimatedFirstDueDateStr}</span>
                        </div>
                    )}
                    {estimatedLastDueDateStr && (
                        <div className="flex justify-between px-4 py-2">
                            <span className="text-gray-500 dark:text-gray-400">Est. Last Due</span>
                            <span className="font-medium dark:text-gray-200">{estimatedLastDueDateStr}</span>
                        </div>
                    )}
                </div>
                <div className="flex gap-2 justify-end">
                    <Button color="slate" onClick={() => setShowConfirm(false)} disabled={formik.isSubmitting}>
                        Go back
                    </Button>
                    <Button
                        color="indigo"
                        isSaving={formik.isSubmitting}
                        disabled={formik.isSubmitting}
                        onClick={handleConfirmSubmit}
                    >
                        {formik.isSubmitting ? 'Creating plan...' : 'Confirm & create'}
                    </Button>
                </div>
            </ConfirmDialogShell>

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center gap-1 rounded-md border border-indigo-600 dark:border-indigo-400 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Incentives
                </button>
            </div>

            <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-orange-100 dark:bg-orange-900/40 shrink-0">
                    <BuildingOffice2Icon className="w-4 h-4 text-orange-700 dark:text-orange-400" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Create {utilityName ?? 'Utility'} Payment Plan
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        Set up a new {utilityName ?? 'Utility'}-sponsored payment plan for this tenant account.
                    </p>
                </div>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-6">
                <Panel header="Plan Configuration">
                    <div className="space-y-5 pt-2">
                        {/* Balance + billing period info panel */}
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-blue-800 dark:text-blue-300">Estimated {utilityName} balance</p>
                                    <p className="text-lg font-semibold text-blue-900 dark:text-blue-200">
                                        {estimatedUtilityBalance !== undefined ? formatCurrency(outstandingBalance) : '—'}
                                    </p>
                                </div>
                                {outstandingBalance > 0 && formik.values.totalAmount !== outstandingBalance.toFixed(2) && amountMode === 'total' && (
                                    <Button
                                        size={1}
                                        color="slate"
                                        onClick={() => formik.setFieldValue('totalAmount', outstandingBalance.toFixed(2))}
                                    >
                                        Reset to balance
                                    </Button>
                                )}
                            </div>
                            {lastKingInvoice?.dueDate && (
                                <p className="text-xs text-blue-700 dark:text-blue-400">
                                    Last billing period:
                                    {lastKingInvoice.billingStartDate && lastKingInvoice.billingEndDate
                                        ? <> {formatShortDate(lastKingInvoice.billingStartDate)} – {formatShortDate(lastKingInvoice.billingEndDate)}</>
                                        : null
                                    }
                                    {' · '}Statement: {formatShortDate(
                                        (() => {
                                            const d = new Date(lastKingInvoice.dueDate!.slice(0, 10) + 'T00:00:00')
                                            d.setDate(d.getDate() - 14)
                                            return d.toISOString().split('T')[0]
                                        })()
                                    )}
                                    {' · '}Due: {formatShortDate(lastKingInvoice.dueDate)}
                                </p>
                            )}
                        </div>

                        {/* First installment due date + estimated last due */}
                        <div className="grid grid-cols-2 gap-4 items-start">
                            <div className="space-y-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    First Installment Due Date <span className="text-red-500">*</span>
                                </label>
                                <Datepicker
                                    useRange={false}
                                    asSingle={true}
                                    value={{
                                        startDate: firstDueDateObj,
                                        endDate: firstDueDateObj,
                                    }}
                                    onChange={(value: any) => {
                                        const picked: Date | string | null = value?.startDate ?? null
                                        if (picked == null) {
                                            formik.setFieldValue('firstDueDate', '')
                                            return
                                        }
                                        const asDate = picked instanceof Date ? picked : new Date(picked)
                                        if (!isNaN(asDate.getTime())) {
                                            formik.setFieldValue('firstDueDate', formatDateForApi(asDate))
                                        }
                                    }}
                                    primaryColor={'indigo'}
                                    inputClassName={classnames(getInputStyles(), 'inline-block w-full')}
                                    popoverDirection="down"
                                    displayFormat={'MMM D, YYYY'}
                                    minDate={todayMidnight}
                                />
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Subsequent installments are due monthly from this date.
                                </p>
                            </div>
                            {estimatedLastDueDateStr && (
                                <DisplayField label="Est. Last Due">
                                    {estimatedLastDueDateStr}
                                </DisplayField>
                            )}
                        </div>

                        {isFirstDueInPast && (
                            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                                <ExclamationCircleIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    First installment due date is in the past. Please choose a date on or after today.
                                </p>
                            </div>
                        )}

                        {/* Duration */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Duration (months) <span className="text-red-500">*</span>
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                {COMMON_DURATIONS.map((months) => (
                                    <button
                                        key={months}
                                        type="button"
                                        onClick={() => {
                                            setShowCustomDuration(false)
                                            formik.setFieldValue('duration', months.toString())
                                        }}
                                        className={`px-3 py-1 rounded-full text-sm border cursor-pointer transition-colors ${!showCustomDuration && formik.values.duration === months.toString()
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                            }`}
                                    >
                                        {months} mo
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCustomDuration(true)
                                        formik.setFieldValue('duration', '')
                                    }}
                                    className={`px-3 py-1 rounded-full text-sm border cursor-pointer transition-colors ${showCustomDuration
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                        }`}
                                >
                                    Other
                                </button>
                                {showCustomDuration && (
                                    <Input
                                        label={undefined}
                                        type="number"
                                        value={formik.values.duration}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        name="duration"
                                        placeholder="months"
                                        className="w-24"
                                        error={formik.touched.duration && formik.errors.duration ? formik.errors.duration : undefined}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Amount Entry Mode */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Amount Entry Mode
                            </label>
                            <div className="flex gap-2 flex-wrap">
                                {(['total', 'per-installment', 'freeform'] as AmountMode[]).map((mode) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => {
                                            setAmountMode(mode)
                                            if (mode !== 'per-installment') setPerInstallmentStr('')
                                        }}
                                        className={`px-3 py-1.5 rounded text-sm border cursor-pointer transition-colors ${amountMode === mode
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                            }`}
                                    >
                                        {mode === 'total' ? 'Total amount' : mode === 'per-installment' ? 'Per installment' : 'Freeform'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {amountMode === 'total' && (
                            <Input
                                label="Total Plan Amount"
                                type="number"
                                value={formik.values.totalAmount}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="totalAmount"
                                placeholder="0.00"
                                required
                                error={formik.touched.totalAmount && formik.errors.totalAmount ? formik.errors.totalAmount as string : undefined}
                            />
                        )}

                        {amountMode === 'per-installment' && (
                            <div>
                                <Input
                                    label="Amount Per Installment ($)"
                                    type="number"
                                    value={perInstallmentStr}
                                    onChange={(e) => setPerInstallmentStr(e.target.value)}
                                    placeholder="0.00"
                                    required
                                />
                                {perInstallmentTotal > 0 && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Total: {formatCurrency(perInstallmentTotal)} ({formik.values.duration} installments × {formatCurrency(parseFloat(perInstallmentStr) || 0)})
                                    </p>
                                )}
                            </div>
                        )}

                        {exceedsBalance && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                                <ExclamationCircleIcon className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700 dark:text-red-300">
                                    Plan amount ({formatCurrency(parseFloat(effectiveTotalStr) || 0)}) may not exceed the estimated {utilityName} balance ({formatCurrency(outstandingBalance)}).
                                </p>
                            </div>
                        )}

                        {/* Installment Configurator */}
                        <InstallmentConfigurator
                            totalAmount={parseFloat(effectiveTotalStr) || 0}
                            count={parseInt(formik.values.duration) || 0}
                            firstDueDate={formik.values.firstDueDate || undefined}
                            installments={customInstallments}
                            onInstallmentsChange={setCustomInstallments}
                            editedIndices={editedIndices}
                            onEditedIndicesChange={setEditedIndices}
                            freeform={amountMode === 'freeform'}
                        />
                    </div>
                </Panel>

                <div className="flex justify-end gap-3">
                    <Button color="slate" onClick={onCancel} disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        color="indigo"
                        disabled={formik.isSubmitting || hasInstallmentIssue || exceedsBalance || isFirstDueInPast || !formik.values.firstDueDate}
                        onClick={handleSubmitClick}
                    >
                        Create plan
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default CreateUtilityPaymentPlanForm
